/**
 * Fixture PROFILES - read from OPBridge, which vendors them.
 *
 * WHY THIS EXISTS (and why it is a 2026-09-17 fix, not a 2026-09-17 feature).
 * `fixture.ts` has said since it was written that the PWA would switch to
 * reading profiles from OPBridge "when OPBridge ships a profiles endpoint".
 * That endpoint shipped at OPBridge Session 141 as `GET /dmx/profiles`. The
 * switch never happened, and profile resolution stayed on `BUILTIN_PROFILES`
 * - a hardcoded table with ONE entry in it, the Chauvet SlimPAR 56.
 *
 * Measured against the operator's actual five-fixture rig on 2026-09-17:
 *
 *     back-left          "Back Left"                        caps=3  resolved
 *     back-right-wash    "Back Right Wash"                  caps=3  resolved
 *     pl60c-2            "PL60C 2"                          caps=0  UNRESOLVED
 *     pl60c-1            "PL60C 1"                          caps=0  UNRESOLVED
 *     colorband-pix-71   "chauvet-dj/colorband-pix · 1.71"  caps=0  UNRESOLVED
 *
 * Three of five fixtures resolved zero capabilities, so nothing capability-
 * aware could render for them at all. And the COLORband fell the whole way
 * down `fromBridgeFixture`'s label chain to a synthesised label carrying its
 * raw `profileId` - the one outcome that function's own docstring forbids,
 * because an unreadable label in a fixture list is how an operator patches
 * the wrong light mid-set. The chain was working exactly as written; the
 * table underneath it was the hole.
 *
 * WHAT THE CATALOGUE ADDS BEYOND CAPABILITIES. Three things the local model
 * had no way to represent, each of which an operator-facing surface needs:
 *   - `archetype` - the broad kind of light, from OPBridge's pinned seven-name
 *     vocabulary. This is how a make/model picker should be grouped, because
 *     it is how the operator thinks about a light before they think about its
 *     brand. OPBridge added the field precisely so consumers stop regexing the
 *     id to guess; they measured that guess wrong on 4 of 7 profiles.
 *   - `subfixtures` - addressable zones. The COLORband PiX has four. Without
 *     them no surface can offer per-zone control, and a whole-fixture write
 *     fans to every zone whether the operator meant that or not.
 *   - `effects` - the fixture's own built-in FX macros (seventeen on the
 *     PL60C). Deliberately kept separate from OPBridge's software effects
 *     (`/dmx/fx-catalog`), because only the software ones can follow the clock.
 *
 * CADENCE AND STATUS MODEL are deliberately not reinvented: this mirrors
 * `bridgeSync.ts` exactly (DEC-131) - fetched on bridge-connect plus explicit
 * refresh, never polled, with a multi-valued status so "empty catalogue",
 * "not allowed to ask" and "no bridge" stay three distinguishable problems.
 * Profiles are build-time const data on OPBridge, so on-connect is if anything
 * more clearly right here than it is for the rig itself.
 *
 * THIS MODULE NEVER WRITES THE LOCAL SEED. `BUILTIN_PROFILES` stays exactly
 * as it is and remains the last-resort fallback for a disconnected iPad on a
 * profile nothing else knows.
 */

import { createSignal, createEffect, untrack, type Accessor } from 'solid-js';
import type {
  FixtureProfile,
  FixtureProfileMode,
  FixtureCapability,
  FixtureCapabilityName,
  FixtureArchetype,
} from '@opbuttons/schema';
import { bridges, getConnection } from '../opbridge/store.ts';
import {
  OpbridgeError,
  type DmxProfile,
  type DmxProfileMode,
  type DmxSoftwareEffect,
} from '../opbridge/httpClient.ts';

/** The seven names OPBridge pins in `ARCHETYPE_VOCABULARY`. Mirrored here so
 *  an unexpected value degrades to `undefined` (rendered as "Other") rather
 *  than becoming a phantom category in a picker. */
const ARCHETYPES: readonly string[] = ['wash', 'strobe', 'bar', 'par', 'mover', 'beam', 'laser'];

function toArchetype(raw: string | null | undefined): FixtureArchetype | undefined {
  if (raw == null) return undefined;
  return ARCHETYPES.includes(raw) ? (raw as FixtureArchetype) : undefined;
}

/**
 * Wire -> app translation for one mode.
 *
 * THE LOSSY PART, NAMED OUT LOUD: the catalogue sends capability NAMES, and
 * the app's `FixtureCapability` wants a `channelIndex` and a `domain` as well.
 * Those are byte offsets, and OPBridge does not send them - deliberately.
 * DEC-014 calls capability-binding "the architectural moat": every DMX write
 * the PWA makes names a capability on a fixture, never a channel number, and
 * OPBridge resolves it. So the PWA has no use for an offset and is not
 * entitled to one.
 *
 * ## `channelIndex` IS `-1`, AND THAT IS THE POINT
 *
 * The obvious fill is the capability's position in the array. Do not: the
 * array is a SET of what the mode can do, not a positional map. OPStage lost
 * a session to exactly this and left the warning in their own source -
 * the PL60C's RGBCW mode lists `["intensity","color.r","color.g","color.b"]`
 * with `channelCount: 8`, but those live at offsets 1,2,3,4 with an unlisted
 * channel at 0, so position-as-offset "rendered the panels' blue as red and
 * their orange as green".
 *
 * The trap is worse than it sounds because it is not uniformly wrong:
 * position-as-offset HOLDS for the 3-channel SlimPAR and for the COLORband's
 * 4x3 blocks, so it tests clean on two of this operator's three profile
 * families and fails silently on the third.
 *
 * `-1` is not a valid offset, so anything that ever tried to use one from a
 * catalogue-derived profile breaks loudly instead of painting the wrong
 * colour. The field stays because the BUILT-IN seed carries real offsets from
 * OPBridge's vendored JSON - one field, two provenances, and only one of them
 * means anything.
 */
function fromWireMode(m: DmxProfileMode): FixtureProfileMode {
  const capabilities: FixtureCapability[] = m.capabilities.map((name) => ({
    capability: name as FixtureCapabilityName,
    channelIndex: -1,
    domain: { kind: 'linear' },
  }));
  return {
    name: m.name,
    channelCount: m.channelCount,
    capabilities,
    ...(m.effects?.length ? { effects: [...m.effects] } : {}),
    ...(m.subfixtures?.length
      ? {
          subfixtures: m.subfixtures.map((s) => ({
            index: s.index,
            name: s.name,
            capabilities: s.capabilities as FixtureCapabilityName[],
          })),
        }
      : {}),
  };
}

export function fromWireProfile(p: DmxProfile): FixtureProfile {
  const archetype = toArchetype(p.archetype);
  return {
    id: p.id,
    name: p.name,
    manufacturer: p.manufacturer,
    model: p.model,
    modes: (p.modes ?? []).map(fromWireMode),
    ...(archetype ? { archetype } : {}),
  };
}

export type ProfileSyncStatus =
  /** Never fetched in this session. */
  | 'idle'
  /** Fetch in flight. */
  | 'loading'
  /** Fetched; the catalogue is authoritative (possibly legitimately empty). */
  | 'ready'
  /** No connected primary OPBridge to ask. */
  | 'no-bridge'
  /** OPBridge refused: our bearer's bundle does not grant `/dmx/**` (403). */
  | 'forbidden'
  /** The endpoint is absent on this daemon (404) - an older bridge. */
  | 'unavailable'
  /** Anything else: auth, network, malformed. */
  | 'error';

const [catalogSig, setCatalog] = createSignal<Record<string, FixtureProfile>>({});
const [statusSig, setStatus] = createSignal<ProfileSyncStatus>('idle');
const [errorSig, setError] = createSignal<string>('');
const [fetchedAtSig, setFetchedAt] = createSignal(0);

const [freeFxSig, setFreeFx] = createSignal<DmxSoftwareEffect[]>([]);
const [musicalFxSig, setMusicalFx] = createSignal<DmxSoftwareEffect[]>([]);

/** Profiles OPBridge vendors, keyed by profileId. Empty unless status is
 *  `ready` - callers must check the status rather than reading empty as
 *  "this daemon has no profiles". */
export const bridgeProfiles: Accessor<Record<string, FixtureProfile>> = catalogSig;
export const profileSyncStatus: Accessor<ProfileSyncStatus> = statusSig;
export const profileSyncError: Accessor<string> = errorSig;
export const profileSyncFetchedAt: Accessor<number> = fetchedAtSig;

/** OPBridge's own software effects, free-running and clock-synced. Empty
 *  until a successful fetch; a surface offering effects should fall back to
 *  the fixture's built-in macros rather than showing an empty list. */
export const softwareEffectsFree: Accessor<DmxSoftwareEffect[]> = freeFxSig;
export const softwareEffectsMusical: Accessor<DmxSoftwareEffect[]> = musicalFxSig;

/** True only when OPBridge answered and its catalogue is the one to trust. */
export function bridgeProfilesAuthoritative(): boolean {
  return statusSig() === 'ready';
}

/** Look one profile up in the live catalogue. Returns undefined when the
 *  catalogue has not answered OR genuinely lacks it - the caller falls back,
 *  it does not get to tell the difference here. `profileSyncStatus()` is
 *  where that distinction lives. */
export function bridgeProfile(profileId: string): FixtureProfile | undefined {
  return catalogSig()[profileId];
}

/** One-line state for a status row. Says what is wrong AND who fixes it,
 *  same contract as `fixtureSyncLabel()`. */
export function profileSyncLabel(): string {
  const n = Object.keys(catalogSig()).length;
  switch (statusSig()) {
    case 'idle':        return 'Not read yet';
    case 'loading':     return 'Reading fixture catalogue from OPBridge...';
    case 'ready':       return `${n} fixture profile${n === 1 ? '' : 's'} from OPBridge`;
    case 'no-bridge':   return 'No OPBridge connected - only built-in fixture profiles';
    case 'forbidden':   return 'OPBridge refused: this bearer lacks /dmx scope - only built-in profiles';
    case 'unavailable': return 'This OPBridge has no /dmx/profiles endpoint - only built-in profiles';
    case 'error':       return `Could not read the fixture catalogue: ${errorSig()} - only built-in profiles`;
    default:            return '';
  }
}

/**
 * Read the catalogue from the primary OPBridge. Never throws: every failure
 * lands as a status, for the same reason `refreshBridgeFixtures` does - a
 * thrown fetch inside a connect handler is a silent failure, and this module
 * exists to stop a silent failure.
 *
 * The FX catalogue is fetched alongside and is deliberately NON-FATAL: an
 * older daemon that has `/dmx/profiles` but not `/dmx/fx-catalog` should give
 * us profiles, not nothing. Its failure leaves the effect lists empty and the
 * profile status `ready`, because profiles genuinely did arrive.
 */
export async function refreshBridgeProfiles(): Promise<void> {
  const primary = bridges().find((b) => b.is_primary) ?? bridges()[0];
  const conn = primary ? getConnection(primary.id) : undefined;
  if (!conn || conn.status() !== 'connected') {
    setStatus('no-bridge');
    setError('');
    return;
  }

  setStatus('loading');
  try {
    const res = await conn.httpClient().listDmxProfiles();
    const next: Record<string, FixtureProfile> = {};
    for (const p of Array.isArray(res.profiles) ? res.profiles : []) {
      if (!p?.id) continue;
      next[p.id] = fromWireProfile(p);
    }
    setCatalog(next);
    setFetchedAt(Date.now());
    setStatus('ready');
    setError('');
  } catch (err) {
    setCatalog({});
    if (err instanceof OpbridgeError) {
      if (err.httpStatus === 403) { setStatus('forbidden'); setError(err.message); return; }
      if (err.httpStatus === 404) { setStatus('unavailable'); setError(err.message); return; }
      setStatus('error');
      setError(`${err.kind}: ${err.message}`);
      return;
    }
    setStatus('error');
    setError((err as Error).message);
    return;
  }

  try {
    const fx = await conn.httpClient().dmxFxCatalog();
    setFreeFx(Array.isArray(fx.free) ? fx.free : []);
    setMusicalFx(Array.isArray(fx.musical) ? fx.musical : []);
  } catch {
    // Non-fatal by design, see this function's doc comment.
    setFreeFx([]);
    setMusicalFx([]);
  }
}

/**
 * Re-read whenever a bridge reaches `connected`. Mirrors
 * `installFixtureSync` down to the `untrack` - without it the signals written
 * inside re-trigger the effect and the app fetches in a loop (the standing
 * Solid trap: an effect that writes a signal it also reads).
 */
export function installProfileSync(): void {
  createEffect(() => {
    const anyConnected = bridges().some((b) => getConnection(b.id)?.status() === 'connected');
    untrack(() => {
      if (anyConnected) {
        void refreshBridgeProfiles();
      } else {
        setStatus('no-bridge');
        setCatalog({});
        setFreeFx([]);
        setMusicalFx([]);
      }
    });
  });
}

// ─── DEV test hook (profile sync state) ───────────────────────────────────
// Same reasoning as `__opbuttonsFixtures`: a smoke must read the SAME live
// module instance the app uses. A `page.evaluate(() => import(...))` re-import
// silently resolves to a SECOND, dead module once Vite's dev server has
// appended an HMR version query to the app's own import, and the smoke then
// polls a copy of a signal nothing ever writes to. Stripped from production
// builds by the `import.meta.env.DEV` gate.
declare global {
  interface Window {
    __opbuttonsFixtureProfiles?: {
      status(): ProfileSyncStatus;
      label(): string;
      authoritative(): boolean;
      count(): number;
      ids(): string[];
      archetypeOf(profileId: string): string | null;
      modeNames(profileId: string): string[];
      freeEffects(): string[];
      musicalEffects(): string[];
    };
  }
}

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.__opbuttonsFixtureProfiles = {
    status: () => statusSig(),
    label: profileSyncLabel,
    authoritative: bridgeProfilesAuthoritative,
    count: () => Object.keys(catalogSig()).length,
    ids: () => Object.keys(catalogSig()).sort(),
    archetypeOf: (id) => catalogSig()[id]?.archetype ?? null,
    modeNames: (id) => (catalogSig()[id]?.modes ?? []).map((m) => m.name),
    freeEffects: () => freeFxSig().map((e) => e.name),
    musicalEffects: () => musicalFxSig().map((e) => e.name),
  };
}
